import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Wrench, Gauge, FileText, Link2, MapPin, Search, BookOpen, Sparkles,
  Globe, Code2, Activity, ListChecks, Tags, ShieldCheck, Network,
  PenTool, BarChart3, Bot, Layers, Target,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "technical-seo": {
    eyebrow: "Search & SEO · SEO",
    title: "Technical SEO",
    sub: "Fix what crawlers see so the rest of your SEO actually works.",
    metaDesc: "Technical SEO audits and fixes: Core Web Vitals, crawl, indexation, schema, internal linking and site architecture.",
    intro:
      "If Google can't crawl, render and understand your site, content and links don't matter. We diagnose and fix the technical layer — speed, indexation, schema, internal linking — so every other SEO investment compounds.",
    features: [
      { icon: Gauge, title: "Core Web Vitals", description: "LCP, CLS and INP optimization across templates." },
      { icon: Code2, title: "Crawl & indexation", description: "Robots, sitemaps, canonical and pagination cleanup." },
      { icon: Layers, title: "Schema markup", description: "Article, Product, FAQ, Breadcrumb and Organization JSON-LD." },
      { icon: Network, title: "Internal linking", description: "Hub-and-spoke linking that ships ranking signal." },
      { icon: Activity, title: "Log file analysis", description: "See what Googlebot actually crawls — and what it skips." },
      { icon: ShieldCheck, title: "Migrations & redirects", description: "Safe replatforming with full 301 mapping and QA." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Crawl, render, logs and Core Web Vitals baseline." },
      { step: "02", title: "Prioritize", description: "Severity-scored backlog tied to traffic impact." },
      { step: "03", title: "Implement", description: "Hands-on fixes with your engineering team." },
      { step: "04", title: "Verify", description: "Search Console + crawl re-test to confirm wins." },
    ],
    deliverables: ["Full technical audit", "Prioritized fix backlog", "Schema templates", "Redirect map", "Core Web Vitals report", "Re-crawl validation"],
    techStack: ["Screaming Frog", "Sitebulb", "Search Console", "PageSpeed Insights", "Lighthouse", "Ahrefs"],
    pricing: [
      { name: "Audit", price: "$2.5k", features: ["Crawl + render audit", "Top 20 findings", "Workshop"] },
      { name: "Audit + fix", price: "$8k", period: "project", highlighted: true, features: ["Audit", "Implementation support", "Re-crawl validation"] },
      { name: "Retainer", price: "$3.5k", period: "month", features: ["Monthly crawls", "Continuous fixes", "Migration support"] },
    ],
    faqs: [
      { q: "Will you work with our dev team?", a: "Yes — we open PRs, write tickets, and pair on implementation." },
      { q: "Can you handle a site migration?", a: "Yes — pre-launch crawl, redirect mapping, staging QA and post-launch monitoring." },
    ],
  },
  "on-page-seo": {
    eyebrow: "Search & SEO · SEO",
    title: "On-page SEO",
    sub: "Optimize every page to match intent and beat the SERP.",
    metaDesc: "On-page SEO: title and meta optimization, content briefs, intent matching, internal links and SERP-feature targeting.",
    intro:
      "We rewrite titles, restructure headings, sharpen content for intent and target the SERP features that actually drive clicks — turning existing pages into ranking, converting assets.",
    features: [
      { icon: Tags, title: "Titles & metas", description: "CTR-optimized titles and descriptions across the site." },
      { icon: ListChecks, title: "Heading & structure", description: "H1–H3 hierarchy aligned to query intent." },
      { icon: FileText, title: "Content optimization", description: "Topic coverage, entities and freshness updates." },
      { icon: Target, title: "Intent matching", description: "Align format and depth to what ranks today." },
      { icon: Sparkles, title: "SERP features", description: "Featured snippets, People Also Ask and image packs." },
      { icon: Network, title: "Internal linking", description: "Contextual links that move authority where it matters." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Score pages by traffic, rank and intent gap." },
      { step: "02", title: "Brief", description: "Per-page optimization briefs and link plans." },
      { step: "03", title: "Implement", description: "Rewrite, restructure, and publish updates." },
      { step: "04", title: "Track", description: "Rank, CTR and traffic deltas in Search Console." },
    ],
    deliverables: ["Page-level audit", "Optimization briefs", "Title/meta rewrites", "Content updates", "Internal link plan", "Performance report"],
    techStack: ["Ahrefs", "Semrush", "Surfer SEO", "Clearscope", "Search Console", "GA4"],
    pricing: [
      { name: "Quick win pack", price: "$1.5k", features: ["10 pages", "Briefs + rewrites", "Internal link plan"] },
      { name: "Site refresh", price: "$6k", period: "project", highlighted: true, features: ["50 pages", "Briefs + implementation", "SERP targeting"] },
      { name: "Retainer", price: "$3k", period: "month", features: ["Monthly optimization sprints", "Content refresh", "Reporting"] },
    ],
    faqs: [
      { q: "Will you change our brand voice?", a: "No — we optimize within your style guide and review with you before publishing." },
      { q: "How do you pick which pages?", a: "Highest opportunity = good ranking + weak CTR or stuck on page 2." },
    ],
  },
  "link-building": {
    eyebrow: "Search & SEO · SEO",
    title: "Link Building",
    sub: "Earn authoritative backlinks the kind of links that actually move rankings.",
    metaDesc: "White-hat link building: digital PR, guest posts, niche edits, broken link reclamation and unlinked mention recovery.",
    intro:
      "We don't buy spam. We earn links from publications and sites in your niche — through digital PR, expert commentary, guest posts and reclamation work — building a backlink profile Google trusts.",
    features: [
      { icon: Link2, title: "Digital PR", description: "Data studies and stories that journalists actually link." },
      { icon: PenTool, title: "Guest posting", description: "Bylined posts on relevant, real-traffic sites." },
      { icon: Search, title: "Unlinked mentions", description: "Convert brand mentions into followed links." },
      { icon: Wrench, title: "Broken link reclamation", description: "Replace dead competitor links with yours." },
      { icon: ShieldCheck, title: "Toxic link cleanup", description: "Audit and disavow what's hurting you." },
      { icon: BarChart3, title: "Link reporting", description: "DR, traffic, anchors and ranking impact monthly." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Baseline backlink profile vs competitors." },
      { step: "02", title: "Target", description: "Prospect list with DR, traffic and topical fit." },
      { step: "03", title: "Outreach", description: "Personalized pitches with strong angles." },
      { step: "04", title: "Report", description: "Acquired links, anchors and ranking lift." },
    ],
    deliverables: ["Backlink audit", "Prospect database", "Outreach campaigns", "Acquired link reports", "Anchor diversification plan", "Disavow file (if needed)"],
    techStack: ["Ahrefs", "Pitchbox", "Hunter", "Respona", "HARO", "Semrush"],
    pricing: [
      { name: "Starter", price: "$2k", period: "month", features: ["4 links / month", "DR 30+", "Niche-relevant"] },
      { name: "Growth", price: "$5k", period: "month", highlighted: true, features: ["10 links / month", "DR 50+", "Digital PR included"] },
      { name: "PR-led", price: "$12k", period: "month", features: ["Data study", "Major-publication links", "PR distribution"] },
    ],
    faqs: [
      { q: "Are these PBNs?", a: "Never. Every link is on a real site with real traffic and editorial standards." },
      { q: "How fast do links impact ranking?", a: "Typically 4–12 weeks after acquisition, depending on competition and authority." },
    ],
  },
  "keyword-research": {
    eyebrow: "Search & SEO · Content",
    title: "Keyword Research",
    sub: "An intent-mapped keyword universe that powers your entire content plan.",
    metaDesc: "Deep keyword research: intent mapping, difficulty scoring, SERP analysis and pillar/cluster planning.",
    intro:
      "We map every query that matters — by intent, difficulty, traffic and business value — and turn it into a pillar-and-cluster plan your content team can execute against for the next 12 months.",
    features: [
      { icon: Search, title: "Keyword universe", description: "Thousands of queries with intent, volume and KD." },
      { icon: Target, title: "Intent mapping", description: "Informational, commercial, transactional and brand." },
      { icon: Layers, title: "Pillar & cluster plan", description: "Topical authority architecture for your domain." },
      { icon: BarChart3, title: "SERP analysis", description: "What ranks today and the format that wins." },
      { icon: ListChecks, title: "Prioritized backlog", description: "Scored by traffic, difficulty and business value." },
      { icon: Globe, title: "Multi-locale", description: "Per-language and per-country keyword sets." },
    ],
    process: [
      { step: "01", title: "Seed", description: "Stakeholder interviews and competitor seed terms." },
      { step: "02", title: "Expand", description: "Mining tools, SERPs and PAA for the full universe." },
      { step: "03", title: "Score", description: "Intent, difficulty, volume and value scoring." },
      { step: "04", title: "Plan", description: "Pillar/cluster map and prioritized publishing backlog." },
    ],
    deliverables: ["Keyword universe spreadsheet", "Intent map", "Pillar & cluster diagram", "SERP analysis", "12-month content backlog", "Workshop handoff"],
    techStack: ["Ahrefs", "Semrush", "Google Keyword Planner", "AlsoAsked", "Keywords Everywhere"],
    pricing: [
      { name: "Focused", price: "$1.5k", features: ["1 topic / locale", "Up to 300 keywords", "Cluster plan"] },
      { name: "Full universe", price: "$5k", period: "project", highlighted: true, features: ["Whole domain", "1000+ keywords", "Pillar/cluster + backlog"] },
      { name: "Multi-locale", price: "$9k", period: "project", features: ["3 locales", "Localized keyword sets", "Per-country plans"] },
    ],
    faqs: [
      { q: "Will you reuse our existing content?", a: "Yes — we map current pages into the cluster plan and flag what to update vs create." },
      { q: "What about brand keywords?", a: "Included — defensive and brand-vs-generic competitive mapping." },
    ],
  },
  "blog-articles": {
    eyebrow: "Search & SEO · Content",
    title: "Blog & Articles",
    sub: "Search-driven articles written by subject-matter experts.",
    metaDesc: "SEO blog and article production: briefs, expert writing, optimization and publishing for organic growth.",
    intro:
      "We brief, write, optimize and publish blog content that ranks and converts — pairing in-house SEO with vetted subject-matter writers across tech, SaaS, finance, health and more.",
    features: [
      { icon: BookOpen, title: "SEO briefs", description: "Intent, entities, outline and SERP-feature targeting." },
      { icon: PenTool, title: "Expert writing", description: "Vetted writers with domain expertise." },
      { icon: FileText, title: "Optimization", description: "On-page tuning before publish — titles, metas, links." },
      { icon: Sparkles, title: "Editorial QA", description: "Fact-checking, voice, originality and Copyscape pass." },
      { icon: Network, title: "Internal linking", description: "Every article wired into your cluster map." },
      { icon: BarChart3, title: "Performance reporting", description: "Ranks, traffic and conversions per article." },
    ],
    process: [
      { step: "01", title: "Brief", description: "SEO + editorial brief from your keyword backlog." },
      { step: "02", title: "Draft", description: "Expert writer drafts, you review." },
      { step: "03", title: "Optimize", description: "On-page tuning, schema, internal links." },
      { step: "04", title: "Publish & report", description: "Push live and track performance monthly." },
    ],
    deliverables: ["SEO briefs", "Long-form articles", "Optimized meta + headings", "Custom imagery", "Internal links", "Performance reports"],
    techStack: ["Surfer SEO", "Clearscope", "Frase", "Grammarly", "Copyscape", "WordPress / MDX"],
    pricing: [
      { name: "Starter", price: "$1.5k", period: "month", features: ["2 articles / month", "1500 words", "SEO + editorial pass"] },
      { name: "Growth", price: "$4k", period: "month", highlighted: true, features: ["6 articles / month", "Up to 2500 words", "Custom imagery"] },
      { name: "Scale", price: "$10k", period: "month", features: ["20 articles / month", "Pillar pieces", "Editorial calendar"] },
    ],
    faqs: [
      { q: "Do you write technical content?", a: "Yes — we have writers across dev tools, fintech, healthcare and more." },
      { q: "Will it sound like us?", a: "We onboard with your style guide and tone samples; final approval is always yours." },
    ],
  },
  "ai-content": {
    eyebrow: "Search & SEO · Content",
    title: "AI Content",
    sub: "AI-assisted content at scale — without sacrificing quality, originality or rankings.",
    metaDesc: "AI content services: human-in-the-loop AI workflows, programmatic SEO, content refreshes and quality controls.",
    intro:
      "AI can scale content production 10× — and tank your rankings if you let it ship raw. We design human-in-the-loop AI workflows: programmatic templates, fact-checking, originality scoring and editorial review so every page is something Google (and your readers) want.",
    features: [
      { icon: Bot, title: "AI workflows", description: "Custom pipelines combining GPT, Claude and Gemini for each step." },
      { icon: Layers, title: "Programmatic SEO", description: "Template-driven pages from structured data — done responsibly." },
      { icon: PenTool, title: "Human-in-the-loop", description: "Editors review, fact-check and polish every draft." },
      { icon: Sparkles, title: "Content refresh", description: "AI-assisted updates of underperforming articles at scale." },
      { icon: ShieldCheck, title: "Originality + safety", description: "Detection, plagiarism and hallucination checks." },
      { icon: BarChart3, title: "Quality scoring", description: "Per-article quality scores tied to ranking outcomes." },
    ],
    process: [
      { step: "01", title: "Design", description: "Pick the right workflow per content type." },
      { step: "02", title: "Pilot", description: "Small batch with editor review and QA." },
      { step: "03", title: "Scale", description: "Roll out with monitoring and feedback loops." },
      { step: "04", title: "Optimize", description: "Tune prompts and templates based on performance." },
    ],
    deliverables: ["Workflow design doc", "Prompt + template library", "Editorial review process", "Quality scoring rubric", "Programmatic page templates", "Performance dashboard"],
    techStack: ["OpenAI GPT", "Anthropic Claude", "Google Gemini", "Surfer SEO", "Originality.ai", "Custom pipelines"],
    pricing: [
      { name: "Pilot", price: "$4k", features: ["Workflow design", "20-article pilot", "Editor review"] },
      { name: "Programmatic", price: "$15k", period: "project", highlighted: true, features: ["Template + pipeline", "Up to 500 pages", "QA + monitoring"] },
      { name: "Managed", price: "$6k", period: "month", features: ["Ongoing production", "Refresh cycles", "Performance reporting"] },
    ],
    faqs: [
      { q: "Will Google penalize AI content?", a: "Not if it's helpful, original and reviewed. Google's policy is about quality, not authorship." },
      { q: "Can you train on our brand?", a: "Yes — we tune prompts and few-shot examples to your voice and style guide." },
    ],
  },
};

export const Route = createFileRoute("/marketing/search-seo/$child")({
  component: SearchSeoChild,
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) return {};
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: `/marketing/search-seo/${params.child}` },
      ],
      links: [{ rel: "canonical", href: `https://coderlity.com/marketing/search-seo/${params.child}` }],
    };
  },
});

function SearchSeoChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
